import { cacheKeys } from "@constants/cache-keys";
import { MIXED_ITEM_DIST_STALE_TIME } from "@constants/stale-times";
import { useAuthContext } from "@features/auth/contexts/AuthContext";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import { useActiveStudyPackCollectionId } from "@hooks/useActiveStudyPackCollectionId";
import { useQuery } from "@tanstack/react-query";
import { getMixedItemDistribution } from "../api/queries";
import type { MixedItemDistAction } from "./types";

type Options = {
	action?: MixedItemDistAction;
	collectionId?: number;
	enabled?: boolean;
	staleTime?: number;
	retry?: boolean;
};

export const getMixedItemDistQueryKey = ({
	userPublicMetadataId,
	action,
	examId,
	collectionId,
}: {
	userPublicMetadataId?: number;
	action: MixedItemDistAction;
	examId?: string;
	collectionId?: number;
}) => [
	cacheKeys.home,
	userPublicMetadataId,
	action,
	examId,
	...(collectionId ? [collectionId] : []),
];

const useMixedItemDist = (options?: Options) => {
	const {
		currentExamContext: { examId },
		isExamContextLoading,
	} = useExamContext();
	const { userPublicMetadataId } = useAuthContext();
	const activeStudyPack = useActiveStudyPackCollectionId();

	const action = options?.action ?? "COMMON_DISTRIBUTION";
	const collectionId = options?.collectionId ?? activeStudyPack.collectionId;

	const result = useQuery({
		queryKey: getMixedItemDistQueryKey({
			userPublicMetadataId,
			action,
			examId,
			collectionId,
		}),
		queryFn: async () =>
			await getMixedItemDistribution({
				userPublicMetadataId: userPublicMetadataId!,
				action,
				examId: examId!,
				collectionId,
			}),
		enabled:
			(options?.enabled ?? true) &&
			!isExamContextLoading &&
			!activeStudyPack.isLoading &&
			Boolean(userPublicMetadataId) &&
			Boolean(examId),
		staleTime: options?.staleTime ?? MIXED_ITEM_DIST_STALE_TIME,
		retry: options?.retry,
	});

	return {
		...result,
		isLoading:
			result.isLoading || isExamContextLoading || activeStudyPack.isLoading,
	};
};

export default useMixedItemDist;
